import React, { useState, useEffect } from "react";
import { Orbit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import MetaConversionsCard from "./MetaConversionsCard";
import { SettingsCard } from "./SettingsCard";
import { useTranslation } from "react-i18next";

interface IntegrationData {
  id?: string;
  account_id: string;
  token_openai: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

const IntegrationsTab: React.FC = () => {
  const { t } = useTranslation("settings");
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [tokenOpenAI, setTokenOpenAI] = useState("");

  // Carregar dados existentes
  const loadIntegrationData = async () => {
    if (!user || !supabase) return;

    try {
      setLoadingData(true);
      
      const { data, error } = await supabase
        .from('account_integrations')
        .select('*')
        .eq('account_id', user.account_id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        throw error;
      }

      if (data) {
        setTokenOpenAI(data.token_openai || "");
        console.log('✅ [IntegrationsTab] Dados carregados:', data);
      }
    } catch (error) {
      console.error('❌ [IntegrationsTab] Erro ao carregar dados:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações de integração' });
    } finally {
      setLoadingData(false);
    }
  };

  // Salvar token OpenAI
  const handleSaveOpenAI = async () => {
    if (!user || !supabase) return;

    setLoading(true);
    setMessage(null);

    try {
      const integrationData: IntegrationData = {
        account_id: user.account_id,
        token_openai: tokenOpenAI.trim() || null,
      };

      const { error } = await supabase
        .from('account_integrations')
        .upsert(integrationData, {
          onConflict: 'account_id',
          ignoreDuplicates: false
        });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Token OpenAI salvo com sucesso!' });
      console.log('✅ [IntegrationsTab] Token OpenAI salvo com sucesso');
    } catch (error) {
      console.error('❌ [IntegrationsTab] Erro ao salvar token:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar token OpenAI' });
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    loadIntegrationData();
  }, [user]);

  if (loadingData) {
    return (
      <div className="space-y-4 @container/inte">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 @container/inte">
      {message && (
        <div className={`p-3 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700 dark:bg-green-500/20 dark:border-green-500/30 dark:text-green-300' 
            : 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-500/20 dark:border-red-500/30 dark:text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      <SettingsCard className="flex flex-col @sm/inte:flex-row items-center gap-4 p-[17px]">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]">
          <Orbit className="h-5 w-5" strokeWidth={2.25} />
        </div>
        <div className="space-y-1 text-center @sm/inte:text-left">
          <h3 className="font-semibold text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("integrations.title")}</h3>
          <p className="text-sm text-[#64748B] dark:text-[#A1A1AA]">
            {t("integrations.subtitle")}
          </p>
        </div>
        <Button className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 min-w-[120px] mt-6 self-center @sm/inte:self-end">
          {t("common.docs")}
        </Button>
      </SettingsCard>

      <SettingsCard className="flex flex-col items-center @sm/inte:flex-row gap-4 p-[17px]">
        <img src="/openai.svg" alt="OpenAI" className="h-10 w-10" />
        <div className="space-y-1 text-center @sm/inte:text-left flex-1">
          <h3 className="font-semibold text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("integrations.chatgpt")}</h3>
          <p className="text-sm text-[#64748B] dark:text-[#A1A1AA]">
            {t("integrations.chatgptHint")}
          </p>
          <Input
            placeholder="sk-..."
            id="chatapi"
            value={tokenOpenAI}
            onChange={(e) => setTokenOpenAI(e.target.value)}
            className="h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] mt-3"
            type="password"
          />
        </div>
        <Button 
          onClick={handleSaveOpenAI}
          disabled={loading}
          className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 min-w-[120px] mt-6 self-center @sm/inte:self-end"
        >
          {loading ? t('integrations.saving') : t('integrations.save')}
        </Button>
      </SettingsCard>

      <MetaConversionsCard />
    </div>
  );
};

export default IntegrationsTab;